import { Stack, Typography } from '@mui/material'
import { DynamicEditor } from 'components/core/editor/editor'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Pencil } from 'lucide-react'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'

type CELChatZeroStateProps = {
    initialExpression?: string
    condensed?: boolean
}

/**
 * Zero state for CEL chat - shows current expression context.
 * When condensed, shows a compact bar suitable for display after conversation starts.
 */
export const CELChatZeroState: FC<CELChatZeroStateProps> = ({
    initialExpression,
    condensed = false,
}) => {
    if (condensed) {
        return <Stack direction="row" spacing={1} alignItems="center">
            <C1Icon icon={Pencil} sx={{ color: 'primary.main' }} />
            <Typography color="text.secondary">
                <FormattedMessage
                    id="copilot_cel_editing_condensed"
                    defaultMessage="Editing rule condition"
                />
            </Typography>
        </Stack>
    }

    return <Stack direction="column" spacing={1} px={2} py={2}>
        <Typography>
            <FormattedMessage
                id="copilot_cel_zero_state_title"
                defaultMessage="Describe the condition you need, I'll generate a CEL expression for you."
            />
        </Typography>
        <Typography variant="h6" component="span">
            <FormattedMessage
                id="copilot_cel_zero_state_expression"
                defaultMessage="Current expression:"
            />
        </Typography>
        <DynamicEditor
            value={initialExpression ?? ''}
            language="cel"
            options={{
                readOnly: true,
                minimap: { enabled: false },
                lineNumbers: 'off',
                scrollBeyondLastLine: false,
                folding: false,
                renderLineHighlight: 'none',
            }}
            minHeight="60px"
            maxHeight="120px"
            enableFetchValidation={false}
        />
    </Stack>
}
